// @ts-strict-ignore
import { faFilterList, faFilterSlash } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";

import { IconButton, Button, Arrange, Typography } from "@aviary";
import type { ButtonAviarySize } from "@aviary/components/Button";
import type { CatalogSearchType } from "@shared/components/CatalogSearch/CatalogSearch";
import { SearchFilterButton } from "@shared/components/CatalogSearch/SearchFilterButton/SearchFilterButton";
import { e2eAttributes } from "@shared/e2eAttributes";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import type { SearchApplyEntryPoint } from "@shared/hooks/useAnalyticsLogger/StoreAnalyticsEntryPoint";
import { CloseFilterEntryPoint } from "@shared/hooks/useAnalyticsLogger/StoreAnalyticsEntryPoint";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { l } from "@shared/locales/i18n";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";

import { ClearFiltersButton } from "../ClearFiltersButton/ClearFiltersButton";

import * as styles from "./FilterButton.styles";

export enum FilterButtonStyle {
  TEXT_AND_ICON,
  RESPONSIVE,
  ICON,
}

interface Props {
  entryPointForLogging?: SearchApplyEntryPoint;
  inputSize?: "normal" | "large";
  showFilterIconIfLessThanWideScreen?: boolean;
  setShowSearchContent?: (boolean) => void;
  isSearchExpanded?: boolean;
  type?: FilterButtonStyle;
  onClearFilters?: () => void;
}

const FilterButton = ({
  entryPointForLogging,
  inputSize,
  showFilterIconIfLessThanWideScreen,
  isSearchExpanded,
  onClearFilters,
  setShowSearchContent = () => undefined,
  type = FilterButtonStyle.RESPONSIVE,
}: Props) => {
  const { activeSearchType, setActiveSearchType } = useCatalogSearch();

  const { t } = useTranslation();
  const { tablet, widescreen } = useBreakpoints();

  const logAnalyticsEvent = useAnalyticsLogger();
  const { isUnauthenticated } = useSharedGlobalConfig();

  const isMobile = tablet.lessThan;

  const filtersActive: boolean = activeSearchType === "filters";
  const shouldLog = entryPointForLogging && !isUnauthenticated;
  const logFilterClosedInDrawer = () => {
    if (shouldLog) {
      logAnalyticsEvent({
        eventName: AnalyticsEvent.STORE_SEARCH_CLOSE_FILTER,
        payload: {
          entry_point: CloseFilterEntryPoint[`${entryPointForLogging}_Catalog_HeaderFilterButton`],
        },
      });
    }
  };

  const toggleOpenSearchFilter = (filter: CatalogSearchType) => {
    if (filter === activeSearchType) {
      setActiveSearchType("");
      logFilterClosedInDrawer();
    } else {
      setActiveSearchType(filter);
    }
    setShowSearchContent(false);
  };

  const renderMobileIconButton = (size: ButtonAviarySize) => {
    return (
      <IconButton
        css={[styles.filterIconButton, activeSearchType === "filters" && styles.activeFilterButton]}
        isIcon
        aria-label={t(l.sharedCommon.Filters)}
        isColor="system"
        isOutlined
        onClick={() => toggleOpenSearchFilter("filters")}
        data-e2e={e2eAttributes.filtersButton}
        size={size}
      >
        <FontAwesomeIcon icon={faFilterList} css={styles.filterIcon} />
      </IconButton>
    );
  };

  const renderIconFilterButton = () => {
    return widescreen.lessThan && showFilterIconIfLessThanWideScreen ? (
      renderMobileIconButton("large")
    ) : (
      <SearchFilterButton
        css={[styles.filtersButton, isSearchExpanded && isMobile && styles.hidden]}
        isActive={filtersActive}
        isSmall={inputSize === "normal"}
        toggleOpenSearchFilter={() => toggleOpenSearchFilter("filters")}
        data-e2e={e2eAttributes.filtersButton}
      >
        {t(l.sharedCatalog.Filters)}
      </SearchFilterButton>
    );
  };

  const renderTextFilterButton = () => {
    if (filtersActive) {
      return (
        !isMobile && (
          <Arrange>
            <Button isColor="system" isOutlined onClick={() => toggleOpenSearchFilter("filters")}>
              <Arrange spacing="xsmall" alignment="center">
                <FontAwesomeIcon icon={faFilterSlash} />
                <Typography>{t(l.sharedCatalog.HideFilters)}</Typography>
              </Arrange>
            </Button>
            <ClearFiltersButton onClearFilters={onClearFilters} />
          </Arrange>
        )
      );
    }

    return (
      <Button
        isColor="system"
        isOutlined
        onClick={() => toggleOpenSearchFilter("filters")}
        css={styles.inActiveTextFilterButton}
      >
        <Arrange spacing="xsmall" alignment="center">
          <FontAwesomeIcon icon={faFilterList} />
          <Typography>{t(l.sharedCatalog.ShowFilters)}</Typography>
        </Arrange>
      </Button>
    );
  };
  const renderIconOnlyButton = () => {
    return widescreen.lessThan && showFilterIconIfLessThanWideScreen
      ? renderMobileIconButton("medium")
      : renderMobileIconButton("large");
  };

  const renderFilterButton = () => {
    switch (type) {
      case FilterButtonStyle.TEXT_AND_ICON:
        return renderTextFilterButton();
      case FilterButtonStyle.RESPONSIVE:
        return renderIconFilterButton();
      case FilterButtonStyle.ICON:
        return renderIconOnlyButton();
    }
  };

  return renderFilterButton();
};

export { FilterButton };
